import {
    afterRenderEffect,
    ComponentRef,
    computed,
    DestroyRef,
    Directive,
    effect,
    ElementRef,
    inject,
    input,
    output,
    signal,
    ViewContainerRef,
} from '@angular/core';

import { addDescribedBy, removeDescribedBy } from 'app/shared/utils/described-by';

import {
    FeatureOnboardingCardComponent,
    FeatureOnboardingPlacement,
} from './components/feature-onboarding-card/feature-onboarding-card.component';
import { FeatureOnboardingStore } from './feature-onboarding.store';

export interface FeatureOnboarding {
    /** One "seen" state per id, installer and browser. */
    id: string;
    image: string;
    titleKey: string;
    descriptionKey: string;
    placement?: FeatureOnboardingPlacement;
}

/** Lets the pointer travel from the trigger to the card's buttons. */
const CLOSE_DELAY_MS = 150;
const FOCUSABLE = 'a[href], button, [tabindex]:not([tabindex="-1"])';
const DOT_CLASS = 'onboarding-dot';

/**
 * What receives focus in a trigger's host, and carries its dot: the host itself, or the button a component host wraps.
 * A component host can be inline or `display: contents`, with no box to draw the dot on.
 */
function focusableTrigger(host: HTMLElement): HTMLElement | null {
    return host.matches(FOCUSABLE) ? host : host.querySelector<HTMLElement>(FOCUSABLE);
}

/**
 * Highlights a new feature on the trigger that opens it, until the installer uses it: a pulsing dot, and a card
 * opened on hover or keyboard focus. Using the trigger, `Discover` or `Ok` marks the feature as seen.
 * On touch there is no hover, so the first tap opens the card instead of running the trigger's action.
 * A `null` onboarding keeps the directive inert.
 *
 * @example
 *   ```html
 *   <button app-button [appFeatureOnboarding]="SITES_MAP_ONBOARDING" (discover)="showMap()" (click)="showMap()">
 *   ```
 */
@Directive({
    selector: '[appFeatureOnboarding]',
    host: {
        '(pointerenter)': 'onPointerEnter($event)',
        '(pointerleave)': 'onPointerLeave($event)',
        '(pointerdown)': 'onPointerDown($event)',
        '(focusin)': 'onFocusIn($event)',
        '(focusout)': 'onFocusOut($event)',
        '(document:keydown.escape)': 'close()',
    },
})
export class FeatureOnboardingDirective {
    readonly onboarding = input.required<FeatureOnboarding | null>({ alias: 'appFeatureOnboarding' });
    readonly discover = output<void>();

    private readonly store = inject(FeatureOnboardingStore);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    private readonly viewContainer = inject(ViewContainerRef);

    private readonly active = computed(() => {
        const onboarding = this.onboarding();
        return !!onboarding && !this.store.isSeen(onboarding.id)();
    });
    private readonly open = signal(false);

    private card?: ComponentRef<FeatureOnboardingCardComponent>;
    private closeTimer?: ReturnType<typeof setTimeout>;
    private hovering = false;
    private restoringFocus = false;
    private pointerType = '';
    private describedTrigger: HTMLElement | null = null;
    private descriptionIds: string[] = [];

    constructor() {
        effect(() => {
            const onboarding = this.onboarding();
            if (!onboarding || !this.active()) {
                // Forget an open card, so it doesn't pop up by itself if the onboarding comes back.
                this.close();
                this.clear();
                return;
            }

            const card = (this.card ??= this.createCard());
            card.setInput('image', onboarding.image);
            card.setInput('titleKey', onboarding.titleKey);
            card.setInput('descriptionKey', onboarding.descriptionKey);
            card.setInput('placement', onboarding.placement ?? 'bottom');
            card.setInput('open', this.open());
        });

        effect((onCleanup) => {
            if (!this.open()) return;

            const closeOutside = (event: Event) => {
                if (!this.contains(event.target)) this.close();
            };
            // The card is placed once when it opens: anything that moves the trigger closes it.
            const close = () => this.close();
            document.addEventListener('pointerdown', closeOutside, true);
            document.addEventListener('scroll', close, true);
            window.addEventListener('resize', close);
            onCleanup(() => {
                document.removeEventListener('pointerdown', closeOutside, true);
                document.removeEventListener('scroll', close, true);
                window.removeEventListener('resize', close);
            });
        });

        // After render: a component host only contains its button once its own view is rendered.
        afterRenderEffect(() => {
            const card = this.active() ? this.card?.instance : undefined;
            (focusableTrigger(this.host) ?? this.host).classList.toggle(DOT_CLASS, !!card);
            this.describeTrigger(card ? [card.headingId, card.descriptionId] : []);
        });

        // Capture phase: the trigger's own click handler may sit on an inner element, which runs before the host's
        // bubbling listeners.
        const onClick = (event: MouseEvent) => this.onClick(event);
        this.host.addEventListener('click', onClick, true);
        inject(DestroyRef).onDestroy(() => {
            this.host.removeEventListener('click', onClick, true);
            clearTimeout(this.closeTimer);
            this.clear();
        });
    }

    protected onPointerEnter(event: PointerEvent): void {
        if (event.pointerType !== 'touch') this.hover(true);
    }

    protected onPointerLeave(event: PointerEvent): void {
        if (event.pointerType !== 'touch') this.hover(false);
    }

    protected onPointerDown(event: PointerEvent): void {
        this.pointerType = event.pointerType;
    }

    protected onFocusIn(event: FocusEvent): void {
        if (!this.restoringFocus && event.target instanceof Element && event.target.matches(':focus-visible')) {
            this.show();
        }
    }

    // Safari does not focus a clicked button, so a click on the card can blur the trigger with no related target.
    protected onFocusOut(event: FocusEvent): void {
        if (!this.hovering && !this.contains(event.relatedTarget)) this.close();
    }

    /** Hands focus back to the trigger when it was inside the card, instead of losing it with the card. */
    protected close(): void {
        clearTimeout(this.closeTimer);
        this.hovering = false;
        const focusInCard = !!this.card?.location.nativeElement.contains(document.activeElement);
        this.open.set(false);
        if (!focusInCard) return;

        this.restoringFocus = true;
        focusableTrigger(this.host)?.focus();
        this.restoringFocus = false;
    }

    private onClick(event: MouseEvent): void {
        // A keyboard activation (`detail` 0) has no pointer behind it, whatever the last pointerdown was.
        const touch = event.detail > 0 && this.pointerType === 'touch';
        this.pointerType = '';
        if (!this.active()) return;

        if (touch && !this.open()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.show();
            return;
        }

        this.markSeen();
    }

    private show(): void {
        if (!this.active()) return;

        clearTimeout(this.closeTimer);
        this.open.set(true);
    }

    private hover(hovering: boolean): void {
        this.hovering = hovering;
        if (hovering) {
            this.show();
            return;
        }

        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => {
            if (!this.keyboardFocusWithin()) this.close();
        }, CLOSE_DELAY_MS);
    }

    // Seen first: the focus handed back to the trigger must not reopen the card.
    private markSeen(): void {
        const onboarding = this.onboarding();
        if (onboarding) this.store.markSeen(onboarding.id);
        this.close();
    }

    private keyboardFocusWithin(): boolean {
        const focused = document.activeElement;
        return this.contains(focused) && !!focused?.matches(':focus-visible');
    }

    /** Lets screen readers announce the new feature with the trigger, keeping the descriptions set by others. */
    private describeTrigger(ids: string[]): void {
        const previous = this.describedTrigger;
        if (previous) this.descriptionIds.forEach((id) => removeDescribedBy(previous, id));

        const trigger = (this.describedTrigger = focusableTrigger(this.host));
        this.descriptionIds = trigger ? ids.filter((id) => addDescribedBy(trigger, id)) : [];
    }

    private contains(target: EventTarget | null): boolean {
        return (
            target instanceof Node &&
            (this.host.contains(target) || !!this.card?.location.nativeElement.contains(target))
        );
    }

    private createCard(): ComponentRef<FeatureOnboardingCardComponent> {
        const card = this.viewContainer.createComponent(FeatureOnboardingCardComponent);
        const element: HTMLElement = card.location.nativeElement;
        card.setInput('anchor', this.host);
        card.instance.hovered.subscribe((hovered) => this.hover(hovered));
        card.instance.discover.subscribe(() => {
            this.discover.emit();
            this.markSeen();
        });
        card.instance.acknowledge.subscribe(() => this.markSeen());
        element.addEventListener('focusout', (event) => this.onFocusOut(event));
        return card;
    }

    private clear(): void {
        this.card?.destroy();
        this.card = undefined;
    }
}
